import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, Button, Grid, Divider } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PeopleIcon from "@mui/icons-material/People";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import GroupsIcon from "@mui/icons-material/Groups";
import HandshakeIcon from "@mui/icons-material/Handshake";
import PlaceIcon from "@mui/icons-material/Place";
import StorageIcon from "@mui/icons-material/Storage";
import AppsIcon from "@mui/icons-material/Apps";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import AssignmentIcon from "@mui/icons-material/Assignment";
import FlagIcon from "@mui/icons-material/Flag";
import { fetchProject, deleteProject } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import DetailSection from "../../components/common/DetailSection";
import DetailField from "../../components/common/DetailField";
import ConfirmDialog from "../../components/common/ConfirmDialog";

const formatDate = (iso) => {
  if (!iso) return "";
  return iso.substring(0, 10).replace(/-/g, "/");
};

export default function ProjectDetailPage() {
  const { businessDomainId, businessUnitId, projectId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const basePath = `/strategy/${businessDomainId}/business-units/${businessUnitId}/projects/${projectId}`;

  const [data, setData] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const loadData = useCallback(async () => {
    try { setData(await fetchProject(projectId)); }
    catch (e) { notify.apiError(e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDelete = async () => {
    try {
      await deleteProject(projectId);
      notify.success(t("message.delete.success"));
      navigate(`/strategy/${businessDomainId}/business-units/${businessUnitId}/projects`);
    } catch (e) { notify.apiError(e); }
    setConfirmOpen(false);
  };

  if (!data) return null;

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{t("project.detail.title")}</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="contained" startIcon={<EditIcon />}
            onClick={() => navigate(`${basePath}/edit`)}>
            {t("common.edit")}
          </Button>
          <Button variant="contained" color="error" startIcon={<DeleteIcon />}
            onClick={() => setConfirmOpen(true)}>
            {t("common.delete")}
          </Button>
        </Box>
      </Box>

      <DetailSection>
        <Grid container spacing={2}>
          <Grid size={12}>
            <DetailField label={t("project.name")} value={data.projectName} />
          </Grid>
          <Grid size={12}>
            <DetailField label={t("project.description")} value={data.projectDescription} />
          </Grid>
          <Grid size={12}>
            <DetailField label={t("project.startDate")} value={formatDate(data.startDate)} />
          </Grid>
          <Grid size={12}>
            <DetailField label={t("project.endDate")} value={formatDate(data.endDate)} />
          </Grid>
          <Grid size={12}>
            <DetailField label={t("dataCategory.businessState")} value={data.businessState ? t(`businessState.${data.businessState}`) : ""} />
          </Grid>
        </Grid>
      </DetailSection>

      <Divider sx={{ my: 3 }} />
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <Button variant="outlined" startIcon={<FlagIcon />}
          onClick={() => navigate(`${basePath}/goals`)}>
          {t("project.goal.list.title")}
        </Button>
      </Box>

      <Divider sx={{ my: 3 }} />
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <Button variant="outlined" startIcon={<PeopleIcon />}
          onClick={() => navigate(`${basePath}/customer-categories`)}>
          {t("project.customerCategory.list.title")}
        </Button>
        <Button variant="outlined" startIcon={<ShoppingCartIcon />}
          onClick={() => navigate(`${basePath}/product-categories`)}>
          {t("project.productCategory.list.title")}
        </Button>
        <Button variant="outlined" startIcon={<GroupsIcon />}
          onClick={() => navigate(`${basePath}/member-categories`)}>
          {t("project.memberCategory.list.title")}
        </Button>
        <Button variant="outlined" startIcon={<HandshakeIcon />}
          onClick={() => navigate(`${basePath}/partner-categories`)}>
          {t("project.partnerCategory.list.title")}
        </Button>
        <Button variant="outlined" startIcon={<PlaceIcon />}
          onClick={() => navigate(`${basePath}/place-categories`)}>
          {t("project.placeCategory.list.title")}
        </Button>
        <Button variant="outlined" startIcon={<StorageIcon />}
          onClick={() => navigate(`${basePath}/data-categories`)}>
          {t("project.dataCategory.list.title")}
        </Button>
        <Button variant="outlined" startIcon={<AppsIcon />}
          onClick={() => navigate(`${basePath}/application-categories`)}>
          {t("project.applicationCategory.list.title")}
        </Button>
        <Button variant="outlined" startIcon={<AccountBalanceIcon />}
          onClick={() => navigate(`${basePath}/financial-asset-categories`)}>
          {t("project.financialAssetCategory.list.title")}
        </Button>
        <Button variant="outlined" startIcon={<AssignmentIcon />}
          onClick={() => navigate(`${basePath}/action-plans`)}>
          {t("project.actionPlan.list.title")}
        </Button>
      </Box>

      <Box sx={{ mt: 3 }}>
        <Button variant="outlined" onClick={() => navigate(`/strategy/${businessDomainId}/business-units/${businessUnitId}/projects`)}>{t("common.back")}</Button>
      </Box>

      <ConfirmDialog
        open={confirmOpen}
        onConfirm={handleDelete}
        onCancel={() => setConfirmOpen(false)}
      />
    </Box>
  );
}
